import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Copy, Check, ArrowLeft } from 'lucide-react';
import { getMyOrders } from '../../services/p2pService';
import { useAuth } from '../../context/AuthContext';

type MainTab = 'ongoing' | 'fulfilled';
type SubFilter = 'all' | 'completed' | 'cancelled' | 'appeal';

const ONGOING_STATUSES = ['pending_payment', 'payment_sent', 'releasing'];
const FULFILLED_MAP: Record<SubFilter, string[]> = {
    all: ['completed', 'cancelled', 'expired', 'disputed'],
    completed: ['completed'],
    cancelled: ['cancelled', 'expired'],
    appeal: ['disputed'],
};

function useCopy() {
    const [copied, setCopied] = useState('');
    const copy = (text: string, key: string) => {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopied(key);
        setTimeout(() => setCopied(''), 2000);
    };
    return { copy, copied };
}

export default function OrderHistory() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { copy, copied } = useCopy();
    const [mainTab, setMainTab] = useState<MainTab>('ongoing');
    const [subFilter, setSubFilter] = useState<SubFilter>('all');
    const [orders, setOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getMyOrders()
            .then(res => setOrders(res.orders ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const filtered = orders.filter(o => {
        if (mainTab === 'ongoing') return ONGOING_STATUSES.includes(o.status);
        return FULFILLED_MAP[subFilter].includes(o.status);
    });

    const statusLabel: Record<string, string> = {
        pending_payment: 'Pending Payment', payment_sent: 'Payment Sent', releasing: 'Releasing',
        completed: 'Completed', cancelled: 'Cancelled', expired: 'Expired', disputed: 'Appeal',
    };
    const statusClass: Record<string, string> = {
        pending_payment: 'pending', payment_sent: 'pending', releasing: 'pending',
        completed: 'completed', cancelled: 'cancelled', expired: 'cancelled', disputed: 'disputed',
    };

    return (
        <div className="p2p-page">
            {/* Sticky header */}
            <div className="p2p-header">
                <button onClick={() => navigate('/home')} style={{ background:'none', border:'none', color:'var(--p2p-text-secondary)', cursor:'pointer', display:'flex', alignItems:'center', padding:'4px 8px 4px 0' }}>
                    <ArrowLeft size={20} />
                </button>
                <span className="p2p-header-title">Orders</span>
                <div />
            </div>

            {/* Scrollable content */}
            <div className="p2p-page-content">
                {/* Main Tabs */}
                <div className="p2p-tabs">
                    <button className={`p2p-tab ${mainTab === 'ongoing' ? 'active' : ''}`} onClick={() => setMainTab('ongoing')}>Ongoing</button>
                    <button className={`p2p-tab ${mainTab === 'fulfilled' ? 'active' : ''}`} onClick={() => setMainTab('fulfilled')}>Fulfilled</button>
                </div>

                {/* Sub-filters */}
                {mainTab === 'fulfilled' && (
                    <div style={{ display:'flex', gap:8, padding:'10px 16px', borderBottom:'1px solid var(--p2p-border-default)', overflowX:'auto' }}>
                        {(['all','completed','cancelled','appeal'] as SubFilter[]).map(f => (
                            <button key={f} onClick={() => setSubFilter(f)} style={{ padding:'5px 14px', borderRadius:6, border:'1px solid', borderColor: subFilter===f ? 'var(--p2p-buy)' : 'var(--p2p-border-input)', background: subFilter===f ? 'var(--p2p-buy-subtle)' : 'transparent', color: subFilter===f ? 'var(--p2p-buy)' : 'var(--p2p-text-muted)', fontSize:13, cursor:'pointer', whiteSpace:'nowrap', textTransform:'capitalize' }}>
                                {f === 'all' ? 'All' : f.charAt(0).toUpperCase()+f.slice(1)}
                            </button>
                        ))}
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <>
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="p2p-skel-card" style={{ flexDirection:'column', gap:8 }}>
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                    <span className="p2p-skel" style={{ width:80, height:13 }} />
                                    <span className="p2p-skel" style={{ width:60, height:13, borderRadius:4 }} />
                                </div>
                                <span className="p2p-skel" style={{ width:140, height:11 }} />
                                <div style={{ display:'flex', justifyContent:'space-between' }}>
                                    <span className="p2p-skel" style={{ width:100, height:11 }} />
                                    <span className="p2p-skel" style={{ width:80, height:11 }} />
                                </div>
                            </div>
                        ))}
                    </>
                ) : filtered.length === 0 ? (
                    <div className="p2p-empty"><p className="p2p-empty-text">No orders here</p></div>
                ) : (
                    <>
                        {filtered.map(order => {
                            const isBuyer = order.buyer?._id === user?._id;
                            const counterparty = isBuyer ? order.seller : order.buyer;
                            const isCompleted = order.status === 'completed';
                            return (
                                <div key={order._id} className="p2p-ad-card" onClick={() => navigate(isCompleted ? `/p2p/order/${order._id}/detail` : `/p2p/order/${order._id}`)}>
                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                                        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                                            <span className={`p2p-badge p2p-badge-${isBuyer?'buy':'sell'}`}>{isBuyer?'Buy':'Sell'}</span>
                                            <span className={`p2p-badge p2p-badge-${statusClass[order.status]??'pending'}`}>{statusLabel[order.status]??order.status}</span>
                                        </div>
                                        <span style={{ fontSize:12, color:'var(--p2p-text-muted)' }}>{new Date(order.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
                                        <div>
                                            <div style={{ fontSize:18, fontWeight:700, color:'var(--p2p-text-primary)', fontVariantNumeric:'tabular-nums' }}>
                                                {order.cryptoAmount?.toFixed(6)} {order.crypto}
                                            </div>
                                            <div style={{ fontSize:13, color:'var(--p2p-text-muted)', marginTop:2 }}>
                                                {order.fiatAmount?.toLocaleString()} {order.fiat} @ {order.price?.toLocaleString()}
                                            </div>
                                        </div>
                                        <button onClick={e => { e.stopPropagation(); navigate(`/p2p/order/${order._id}`); }} style={{ background:'none', border:'1px solid var(--p2p-border-input)', borderRadius:6, padding:'6px 10px', color:'var(--p2p-text-secondary)', cursor:'pointer', display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
                                            <MessageSquare size={12} /> Chat
                                        </button>
                                    </div>
                                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
                                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                                            <span style={{ fontSize:12, color:'var(--p2p-text-muted)', fontFamily:'monospace' }}>#{order.orderNumber}</span>
                                            <button onClick={e => { e.stopPropagation(); copy(order.orderNumber, order._id); }} className="p2p-copy-btn">
                                                {copied === order._id ? <Check size={12} /> : <Copy size={12} />}
                                            </button>
                                        </div>
                                        {counterparty && (
                                            <span style={{ fontSize:12, color:'var(--p2p-text-muted)' }}>{counterparty.nickname ?? counterparty.name}</span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}
